import axios from 'axios';

// Use REACT_APP_BACKEND_URL if set, otherwise use current origin (for production with custom domain)
const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Simple logger that only logs in development
const isDev = process.env.NODE_ENV === 'development';
const log = {
  error: (...args) => { if (isDev) console.error(...args); }
};

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add response interceptor for better error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Log error for debugging (only in development)
    log.error('API Error:', error.response?.status, error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Get API URL for direct calls
export const getApiUrl = () => API_URL;

// Helper function to make API calls (replaces safeFetch)
export const apiGet = async (endpoint) => {
  try {
    const response = await api.get(endpoint);
    return response.data;
  } catch (error) {
    log.error('API GET error:', error);
    throw error;
  }
};

export const apiPost = async (endpoint, data) => {
  try {
    const response = await api.post(endpoint, data);
    return response.data;
  } catch (error) {
    log.error('API POST error:', error);
    throw error;
  }
};

// Pré-inscrições (próxima edição)
export const preInscricoesApi = {
  create: (data) => api.post('/pre-inscricoes', data),
  list: (params) => api.get('/pre-inscricoes', { params }),
  stats: (params) => api.get('/pre-inscricoes/stats', { params }),
  duplicados: () => api.get('/pre-inscricoes/duplicados'),
  get: (id) => api.get(`/pre-inscricoes/${id}`),
  update: (id, data) => api.patch(`/pre-inscricoes/${id}`, data),
  delete: (id) => api.delete(`/pre-inscricoes/${id}`),
  exportCsvUrl: (params = {}) => `${API_URL}/api/pre-inscricoes/export/csv?${new URLSearchParams(params)}`,
};

// Edição atual (configuração pública do site)
export const edicaoApi = {
  get: () => api.get('/settings/edicao'),
  update: (data) => api.put('/settings/edicao', data),
};

// Galeria de eventos
export const galeriaApi = {
  publica: () => api.get('/galeria/publica'),
  albumPublico: (id) => api.get(`/galeria/publica/albuns/${id}`),
  edicoes: () => api.get('/galeria/edicoes'),
  criarEdicao: (data) => api.post('/galeria/edicoes', data),
  editarEdicao: (id, data) => api.put(`/galeria/edicoes/${id}`, data),
  excluirEdicao: (id) => api.delete(`/galeria/edicoes/${id}`),
  albuns: (edicaoId) => api.get('/galeria/albuns', { params: edicaoId ? { edicao_id: edicaoId } : {} }),
  criarAlbum: (data) => api.post('/galeria/albuns', data),
  editarAlbum: (id, data) => api.put(`/galeria/albuns/${id}`, data),
  reordenarAlbuns: (ids) => api.put('/galeria/albuns/reordenar', { ids }),
  fotos: (albumId) => api.get(`/galeria/albuns/${albumId}/fotos`),
  upload: (albumId, formData, onProgress) => api.post(`/galeria/albuns/${albumId}/fotos`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress: onProgress, timeout: 300000 }),
  reordenarFotos: (albumId, ids) => api.put(`/galeria/albuns/${albumId}/fotos/reordenar`, { ids }),
  editarFoto: (id, data) => api.patch(`/galeria/fotos/${id}`, data),
  excluirFoto: (id) => api.delete(`/galeria/fotos/${id}`),
};

// Eventos API
export const eventosApi = {
  list: () => api.get('/eventos'),
  get: (id) => api.get(`/eventos/${id}`),
  getPublico: (id) => api.get(`/eventos/${id}/publico`),
  create: (data) => api.post('/eventos', data),
  update: (id, data) => api.put(`/eventos/${id}`, data),
  delete: (id) => api.delete(`/eventos/${id}`),
};

// Lotes API
export const lotesApi = {
  list: (eventoId) => api.get('/lotes', { params: { evento_id: eventoId } }),
  get: (id) => api.get(`/lotes/${id}`),
  create: (data) => api.post('/lotes', data),
  update: (id, data) => api.put(`/lotes/${id}`, data),
  delete: (id) => api.delete(`/lotes/${id}`),
};

// Participantes API
export const participantesApi = {
  list: (params) => api.get('/participantes', { params }),
  get: (id) => api.get(`/participantes/${id}`),
  create: (data) => api.post('/participantes', data),
  update: (id, data) => api.put(`/participantes/${id}`, data),
  delete: (id) => api.delete(`/participantes/${id}`),
  updateStatus: (id, data) => api.patch(`/participantes/${id}/status`, data),
  resendEmail: (id) => api.post(`/participantes/${id}/reenviar-email`),
  exportCsv: (params) => {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([_, v]) => v && v !== 'all')
    ).toString();
    return `${API_URL}/api/admin/export/participantes${queryString ? `?${queryString}` : ''}`;
  },
};

// Pagamentos API
export const pagamentosApi = {
  list: () => api.get('/admin/mercadopago-logs'),
  get: (id) => api.get(`/admin/mercadopago-logs/${id}`),
};

// Checkout API
export const checkoutApi = {
  createSession: (participanteId, originUrl) => 
    api.post('/checkout/session', { participante_id: participanteId, origin_url: originUrl }),
  getStatus: (sessionId) => api.get(`/checkout/status/${sessionId}`),
};

// Dashboard API
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
};

export default api;
